/**
 * Ability Context and Provider
 *
 * Route and Can-style gates delegate to the Access Decision (`useAccess` / Session JSON).
 * UI does not build a parallel CASL Ability from a roles array.
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { AppAbility } from '@/components/authnz/types/rbac';
import { abilityFromDecision, makeDecision, useAccess } from '@/lib/access/client';
import { useAuth } from '@/contexts/AuthContext';

const PUBLIC_AUTH_EXEMPT_PATHS = [
  '/login',
  '/signup-required',
  '/purchase',
  '/welcome',
] as const;

function isPublicAuthExemptPath(pathname: string): boolean {
  return PUBLIC_AUTH_EXEMPT_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

const guestDecision = makeDecision({
  uid: null,
  email: null,
  emailKey: null,
  existence: 'none',
  reason: { kind: 'invalid-credential' },
  roles: [],
});

/**
 * Context value type that includes both ability and loading state
 */
interface AbilityContextValue {
  ability: AppAbility;
  loading: boolean;
}

/**
 * Context holding the current user's abilities and loading state
 */
export const AbilityContext = createContext<AbilityContextValue>({
  ability: abilityFromDecision(guestDecision),
  loading: false,
});

/**
 * Convenient Can component for declarative permission checks
 * Usage: <Can I="read" a="Client">...</Can>
 */
export const Can: React.FC<{
  I: Parameters<AppAbility['can']>[0];
  a: Parameters<AppAbility['can']>[1];
  children: React.ReactNode;
  passThrough?: boolean;
}> = ({ I, a, children, passThrough = false }) => {
  const { decision } = useAccess();
  const canPerform = decision.can(I, a);

  if (passThrough) {
    return <>{children}</>;
  }

  return canPerform ? <>{children}</> : null;
};

/**
 * Custom hook to access the ability object (Decision-backed `can` only)
 */
export function useAbility(): AppAbility {
  return useContext(AbilityContext).ability;
}

/**
 * Custom hook to check if abilities are still loading
 */
export function useAbilityLoading(): boolean {
  return useContext(AbilityContext).loading;
}

/**
 * Custom hook to check if user can perform an action
 * Usage: const canEdit = useCanAccess('update', 'Client');
 */
export function useCanAccess(
  action: Parameters<AppAbility['can']>[0],
  subject: Parameters<AppAbility['can']>[1],
): boolean {
  const { decision } = useAccess();
  return decision.can(action, subject);
}

/**
 * Custom hook to get the current user's role
 * Now derives from server session instead of querying Firestore
 */
export function useUserRole() {
  const { user: currentUser, serverUser } = useAuth();
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setRole(null);
      setLoading(false);
      return;
    }

    if (serverUser?.roles && serverUser.roles.length > 0) {
      setRole(serverUser.roles[0]);
    } else {
      setRole(null);
    }
    setLoading(false);
  }, [currentUser, serverUser?.roles]);

  return { role, loading };
}

interface AbilityProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that manages user abilities from the Access Decision
 */
export function AbilityProvider({ children }: AbilityProviderProps) {
  const location = useLocation();
  const {
    user: currentUser,
    serverUser,
    decisionJson,
    loading: authLoading,
  } = useAuth();
  const { decision, loading: accessLoading } = useAccess();
  const allowPublicRouteWhileLoading = isPublicAuthExemptPath(location.pathname);

  const sessionPending = !!currentUser && !serverUser && !decisionJson;

  const ability = useMemo(() => abilityFromDecision(decision), [decision]);

  const finalLoading = authLoading || accessLoading || sessionPending;
  const providerLoading = finalLoading && !allowPublicRouteWhileLoading;

  if (providerLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <AbilityContext.Provider value={{ ability, loading: providerLoading }}>
      {children}
    </AbilityContext.Provider>
  );
}
